import * as fx from '../audio/fx';
import * as engine from '../app/engine';
import { app, on } from '../app/state';
import { CLAMPS, TEXT_SCALE_STEP } from '../core/constants';
import { snapScale } from '../core/storage';
import type { Layout, Theme, Typeface } from '../core/types';
import { announce } from './announcer';
import { onEscape, setTheme } from './chrome';
import { $, $$ } from './dom';
import { applyLayout, initLayout } from './layout';
import { onTabShown, openSheet } from './sheet';
import { applyTextScale, applyTypeface, preloadAllTypefaces } from './typefaces';

/**
 * Look tab + the header's quick menu. Both are views of the same three settings (layout, type
 * style, text size) plus the light/dark theme; every change applies live and is saved.
 */
const s = () => app.data.settings;
const LAYOUT_NAME: Record<Layout, string> = {
  minimal: 'Minimal',
  desk: 'Desk',
  logbook: 'Logbook',
  horizon: 'Horizon',
  flip: 'Flip',
  strip: 'Strip',
};

const radios = (name: string) => $$<HTMLInputElement>(`input[type="radio"][name="${name}"]`);
const pop = $('#lookPop');
const popBtn = $('#themeBtn');
const range = $<HTMLInputElement>('#sizeRange');
let appliedType: Typeface | null = null;

function paint() {
  const st = s();
  for (const [name, value] of [
    ['layout', st.layout],
    ['qLayout', st.layout],
    ['typeface', st.typeface],
    ['theme', st.theme],
    ['qTheme', st.theme],
  ] as const)
    for (const r of radios(name)) r.checked = r.value === value;
  range.value = String(st.textScale);
  range.setAttribute('aria-valuetext', `${st.textScale} percent`);
  for (const id of ['#sizeVal', '#qSizeVal']) $(id).textContent = `${st.textScale}%`;
  const [lo, hi] = CLAMPS.textScale;
  for (const id of ['#sizeDown', '#qSizeDown']) $<HTMLButtonElement>(id).disabled = st.textScale <= lo;
  for (const id of ['#sizeUp', '#qSizeUp']) $<HTMLButtonElement>(id).disabled = st.textScale >= hi;
  $<HTMLButtonElement>('#sizeReset').disabled = st.textScale === 100;
}

/** Applies the saved look to the page (also on cross-tab sync and backup import). */
function apply() {
  const st = s();
  applyLayout(st.layout);
  if (st.typeface !== appliedType) {
    appliedType = st.typeface;
    applyTypeface(st.typeface);
  }
  applyTextScale(st.textScale);
  paint();
}

function setScale(v: number) {
  const next = snapScale(v);
  if (next === s().textScale) return;
  engine.setSetting('textScale', next);
  fx.tick();
}

function bindRadios(name: string, onPick: (value: string, input: HTMLInputElement) => void) {
  for (const r of radios(name))
    r.addEventListener('change', () => {
      if (r.checked) onPick(r.value, r);
    });
}

function pickTheme(t: string, input: HTMLInputElement) {
  if (t === s().theme) return;
  const box = (input.closest('label') ?? input).getBoundingClientRect();
  setTheme(t as Theme, { x: box.left + box.width / 2, y: box.top + box.height / 2 });
}

function pickLayout(l: string) {
  if (l === s().layout) return;
  engine.setSetting('layout', l as Layout);
  fx.tap();
  announce(`${LAYOUT_NAME[l as Layout]} layout`);
}

/* ---------- quick menu ---------- */
function setPop(open: boolean) {
  pop.hidden = !open;
  popBtn.setAttribute('aria-expanded', String(open));
  if (open) pop.querySelector<HTMLInputElement>('input:checked')?.focus();
}
const closePop = () => {
  if (pop.hidden) return false;
  setPop(false);
  popBtn.focus();
  return true;
};

export function initAppearance() {
  initLayout();
  bindRadios('layout', pickLayout);
  bindRadios('qLayout', pickLayout);
  bindRadios('theme', pickTheme);
  bindRadios('qTheme', pickTheme);
  bindRadios('typeface', (t) => {
    if (t === s().typeface) return;
    engine.setSetting('typeface', t as Typeface);
    fx.tap();
  });

  // live while dragging, saved when released
  range.addEventListener('input', () => {
    const v = Number(range.value);
    applyTextScale(v);
    for (const id of ['#sizeVal', '#qSizeVal']) $(id).textContent = `${v}%`;
  });
  range.addEventListener('change', () => setScale(Number(range.value)));
  const step = (d: number) => () => setScale(s().textScale + d * TEXT_SCALE_STEP);
  for (const id of ['#sizeDown', '#qSizeDown']) $(id).addEventListener('click', step(-1));
  for (const id of ['#sizeUp', '#qSizeUp']) $(id).addEventListener('click', step(1));
  $('#sizeReset').addEventListener('click', () => setScale(100));

  popBtn.addEventListener('click', () => setPop(pop.hidden !== false));
  $('#lookMore').addEventListener('click', () => {
    setPop(false);
    openSheet('look');
  });
  document.addEventListener('pointerdown', (e) => {
    if (!pop.hidden && !$('.lookwrap').contains(e.target as Node)) setPop(false);
  });
  // Tabbing out closes it. (A press inside blurs to <body> first — relatedTarget null — so ignore that.)
  pop.addEventListener('focusout', (e) => {
    const to = e.relatedTarget as Node | null;
    if (!pop.hidden && to && !$('.lookwrap').contains(to)) setPop(false);
  });
  onEscape(closePop);

  // the type cards preview each style in its own font
  onTabShown((t) => {
    if (t === 'look') void preloadAllTypefaces();
  });

  on('settings', apply);
  apply();
}
